import React from 'react';

import Autoplay from 'embla-carousel-autoplay';
import {
  Title,
  useAdaptive,
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@skillarea/lib-frontend';
import { GameReturnDto } from '@skillarea/lib-shared';
import { Trans } from 'react-i18next';

import { GameCard } from '../GameCard/GameCard';

import styles from './MoreGames.module.scss';

interface IMoreGamesProps {
  games?: GameReturnDto[];
  isLoading?: boolean;
  currentGameSlug?: string;
}

export const MoreGames: React.FC<IMoreGamesProps> = ({
  currentGameSlug,
  games = [],
  isLoading = false,
}) => {
  const { isDesktop, isMobile, isTablet } = useAdaptive();

  const isAdaptiveResolved = isMobile || isTablet || isDesktop;
  const isMobileViewport = isAdaptiveResolved ? isMobile : false;
  const isTabletViewport = isAdaptiveResolved ? isTablet : false;

  const [api, setApi] = React.useState<CarouselApi>();

  const plugin = React.useRef(
    Autoplay({ delay: 15000, stopOnInteraction: true }),
  );

  const filteredGames = games.filter((g) => {
    if (isMobileViewport && currentGameSlug) {
      return g.slug !== currentGameSlug;
    }
    return true;
  });

  const moreGames = isDesktop ? filteredGames.slice(0, 4) : filteredGames;

  const size = isMobileViewport ? 's' : isTabletViewport ? 'm' : 'l';

  React.useEffect(() => {
    if (!api) {
      return;
    }
    const checkScrollable = () => {
      const canScroll = api.canScrollNext() || api.canScrollPrev();
      if (!canScroll) {
        plugin.current.stop();
      }
    };
    checkScrollable();
    api.on('reInit', checkScrollable);
    return () => {
      api.off('reInit', checkScrollable);
    };
  }, [api]);

  if (!isAdaptiveResolved) {
    return null;
  }

  if (!isLoading && moreGames.length === 0) {
    return null;
  }

  const renderCarousel = () => (
    <div className={styles.carouselWrapper}>
      <Carousel
        className={styles.carousel}
        onMouseEnter={plugin.current.stop}
        onMouseLeave={plugin.current.reset}
        opts={{
          align: 'start',
          loop: true,
        }}
        plugins={[plugin.current]}
        setApi={setApi}
      >
        <CarouselContent className={styles.carouselContent}>
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => {
                const key = `skeleton-carousel-${String(index)}`;
                return (
                  <CarouselItem className={styles.carouselItem} key={key}>
                    <div className={styles.gameCardSkeleton} />
                  </CarouselItem>
                );
              })
            : moreGames.map((game) => (
                <CarouselItem className={styles.carouselItem} key={game.slug}>
                  <GameCard {...game} />
                </CarouselItem>
              ))}
        </CarouselContent>
        <CarouselPrevious className={styles.carouselControlPrev} />
        <CarouselNext className={styles.carouselControlNext} />
      </Carousel>
    </div>
  );

  return (
    <section className={styles.moreGames}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Title color="contrast" size={size} variant="benzin">
            <Trans
              components={{
                accent: <Title color="orange" isInsertion size={size} />,
              }}
              i18nKey="ui.moreGames"
              ns="games"
            />
          </Title>
        </div>

        {!isDesktop ? (
          renderCarousel()
        ) : (
          <div className={styles.gamesGrid}>
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => {
                  const key = `skeleton-grid-${String(index)}`;
                  return <div className={styles.gameCardSkeleton} key={key} />;
                })
              : moreGames.map((game) => <GameCard {...game} key={game.slug} />)}
          </div>
        )}
      </div>
    </section>
  );
};
